'use client'

import SurveyActionBar from '@/components/embedding/survey/SurveyActionBar'
import type { SurveySheetState } from '@/components/embedding/survey/useSurveySheet'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { X } from 'lucide-react'
import { FC } from 'react'
import { PCSurveySection } from '../ModalSurvey'
import InfoCTAButton from './InfoCTAButton'
import VideoInfoContent from './VideoInfoContent'
import VideoInfoPanelStyles from './VideoInfoPanelStyles'

export interface VideoInfoPanelProps {
  currentNode: ScenarioNodeWithVideoUrl
  onClose: () => void
  ctaUrl?: string | null
  ctaLabel?: string | null
  onTrackCtaClick?: () => void
  /** CVで別タブを開く直前に動画を止める */
  onPauseVideo?: () => void
  survey?: SurveySheetState
}

const VideoInfoPanel: FC<VideoInfoPanelProps> = ({
  currentNode,
  onClose,
  ctaUrl,
  ctaLabel,
  onTrackCtaClick,
  onPauseVideo,
  survey,
}) => {
  // アンケート回答に集中させるため、設問シート展開中は動画情報（タイトル/出演者/本文）と
  // CTA を隠してアンケートだけを表示する（#244）。任意設問のオプトインバー待機中
  // （survey.showTab）は情報を隠さず、実際に展開されたときのみ隠す。
  const surveyExpanded = Boolean(survey?.expanded && survey?.activeQuestion)
  // 任意設問の待機中。下端スタックにオプトインバーを出す。
  const showSurveyOptIn = Boolean(survey?.showTab)

  return (
    <>
      <VideoInfoPanelStyles />

      <div className="info-panel">
        <button type="button" className="info-close-button" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        <div
          className={[
            'info-content',
            surveyExpanded ? 'info-content--survey-only' : '',
            // 下端スタックが2段（アンケート＋CV）になるぶん、本文の下余白を広げる
            !surveyExpanded && showSurveyOptIn && ctaUrl ? 'info-content--stack-2' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {/* 展開中の設問シートはパネル全面（info-content--survey-only）に広げるので最上部に置く */}
          {survey && surveyExpanded && <PCSurveySection survey={survey} />}

          {!surveyExpanded && <VideoInfoContent currentNode={currentNode} />}
        </div>

        {/* 下端の追従スタック（アンケート → CV）。本文の途中にオプトインを置くと、
            テキストが短いとCVとの間が間延びし、長いとスクロールしないと現れない。
            動画個別ページ・SPと同じく下端に積んで、文章量に左右されない導線にする。 */}
        {!surveyExpanded && (showSurveyOptIn || ctaUrl) && (
          <>
            <div className="info-bottom-fade" />
            <div className="info-bottom-stack">
              {showSurveyOptIn && survey && (
                <SurveyActionBar total={survey.progress.total} completed={false} available onOpen={survey.onOpenTab} />
              )}
              {ctaUrl && (
                <InfoCTAButton
                  ctaUrl={ctaUrl}
                  ctaLabel={ctaLabel}
                  onTrackCtaClick={onTrackCtaClick}
                  onPauseVideo={onPauseVideo}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default VideoInfoPanel
